import React, { useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';

import { OnboardingNudge } from '@confluence/onboarding-nudge-tooltip';
import { fg } from '@confluence/feature-gating';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import SpotlightImage from '../assets/SpotlightImage.svg';

import { useIsAIFCOnbardingEligible } from './hooks/useIsAIFCOnbardingEligible';

const styles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'& [role="presentation"]::before, & [role="presentation"]::after': {
			boxShadow: `0 0 0 2px ${token('color.border.accent.purple')}`,
		},
	},
});
const Wrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
	<Box xcss={styles.root}>{children}</Box>
);

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.title',
		defaultMessage: 'Use Rovo AI to create',
		description: 'Title for smart create with AI spotlight',
	},
	smartCreateDescription: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.description',
		defaultMessage: 'Start with more than just a blank page or whiteboard using Rovo AI.',
		description: 'Description text for smart create with AI spotlight',
	},
	smartCreateCTA: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.cta',
		defaultMessage: 'Start creating',
		description: 'CTA button text for smart create with AI spotlight',
	},
});

export const AISmartCreateSpotlight = ({
	children,
	source,
	handleClick,
}: {
	children: React.ReactNode | ((isSpotlightActive: boolean) => React.ReactNode);
	handleClick: () => void;
	source: string;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const {
		isAIFCOnbardingEligible,
		loading: isAIFCOnbardingEligibleLoading,
		error: isAIFCOnbardingEligibleError,
	} = useIsAIFCOnbardingEligible();

	const { data, loading, error } = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { hasSeenAIFCSpotlight, isAIFCSpotlightActive } = deserializeState(data);

	const setHasSeenSpotlightWithAnalytics = () => {
		setOnboardingState([
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIFCSpotlight,
				value: 'true',
			},
			{
				key: AIFC_ONBOARDING_STATE_KEYS.isAIFCSpotlightActive,
				value: 'false',
			},
		]);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'spotlight',
				actionSubjectId: 'AISmartCreateSpotlight',
				source,
			},
		}).fire();
	};

	const onCTAClickWithAnalytics = () => {
		onClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AISmartCreateSpotlightCTA',
				source,
			},
		}).fire();
	};

	const onClick = () => {
		setHasSeenSpotlightWithAnalytics();
		handleClick();
	};

	useEffect(() => {
		if (
			!isAIFCOnbardingEligibleLoading &&
			!isAIFCOnbardingEligibleError &&
			isAIFCOnbardingEligible &&
			!loading &&
			!error &&
			!hasSeenAIFCSpotlight
		) {
			setOnboardingState({
				key: AIFC_ONBOARDING_STATE_KEYS.isAIFCSpotlightActive,
				value: 'true',
			});
		}
	}, [
		isAIFCOnbardingEligible,
		hasSeenAIFCSpotlight,
		source,
		loading,
		error,
		setOnboardingState,
		isAIFCOnbardingEligibleLoading,
		isAIFCOnbardingEligibleError,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		if (isAIFCSpotlightActive) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'AISmartCreateSpotlight',
					attributes: {
						source,
					},
				},
			}).fire();
		}
	}, [isAIFCSpotlightActive, source, createAnalyticsEvent]);

	return isAIFCOnbardingEligible ? (
		<OnboardingNudge
			hidden={!isAIFCSpotlightActive}
			setHidden={() => {}}
			heading={formatMessage(i18n.smartCreateTitle)}
			content={formatMessage(i18n.smartCreateDescription)}
			onClick={() => onClick()}
			actions={[
				{
					text: formatMessage(i18n.smartCreateCTA),
					onClick: () => onCTAClickWithAnalytics(),
				},
			]}
			image={SpotlightImage}
			source="onboarding-ai-first-create"
			wrapper={Wrapper}
			actionSubjectId="aiSmartCreate"
			width={275}
			offset={[260, 8]}
			position={source.includes('ContextualCreate') ? 'right-end' : 'left-end'}
			onOutsideClick={() => setHasSeenSpotlightWithAnalytics()}
		>
			{typeof children === 'function' ? children(isAIFCSpotlightActive) : children}
		</OnboardingNudge>
	) : (
		<>{typeof children === 'function' ? children(false) : children}</>
	);
};
